import { put, all, call, takeLatest, takeEvery } from 'redux-saga/effects';
import {
  setClientContactData,
  getClientContactData,
  addClientContact,
  editClientContact,
  setClientSubProjectNetworkError,
  setClientWorkstreamContactsData,
  getClientWorkstreamContactsData,
  getClientWorkstreamsData,
  setClientWorkstreamsLoading,
  setClientWorkstreamsData,
} from './actions';
import {
  addClientContactStore,
  editClientContactStore,
  setClientContactDataLoading,
  setEditClientDataState,
  setClientWorkstreamDataLoading,
} from './reducers';
import { DataState } from '@shared/enums/DataState';
import { getErrorInfo } from '@shared/helpers/getErrorInfo';
import { ClientProjectData } from '@modules/EvaluationModule/interfaces/ClientProjectData';
import { ClientContactData } from '@modules/EvaluationModule/interfaces/ClientSubProjectData';
import { ClientWorkstreamData } from '@modules/EvaluationModule/interfaces/ClientWorkstreamData';
import { displayNotification } from '@modules/App/redux/notifications/actions';
import {
  postClientContact,
  fetchProjectListData,
  patchClientContact,
  fetchClientWorkstreamContactsData,
  fetchClientWorkstreamsData,
} from '@modules/EvaluationModule/api/clientContactData';
import { AxiosResponse } from 'axios';
import { trackUserEvent } from '@modules/App/api/currentUser';
import { pseudoWorkstreamId } from '../helper/mergeWorkstreams';

function* getClientContactDataAsync() {
  try {
    yield put(setClientContactDataLoading());
    const response: ClientProjectData[] = yield call(fetchProjectListData);
    const sortedResponse = [...response].sort((a, b) =>
      a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase())
    );
    yield put(setClientContactData({ data: sortedResponse, state: DataState.Fulfilled }));
  } catch (e) {
    yield put(
      setClientContactData({
        data: null,
        state: DataState.Rejected,
        error: getErrorInfo(e),
      })
    );
  }
}

export function* getClientWorkstreamsDataAsync({ payload }: ReturnType<typeof getClientWorkstreamsData>) {
  if (!payload?.clientId) {
    return;
  }
  const pseudo = { workstreamId: pseudoWorkstreamId(payload.clientId), clientId: payload.clientId };
  yield put(setClientWorkstreamsLoading(payload));
  yield put(setClientWorkstreamDataLoading(pseudo));
  try {
    const department: ClientProjectData & { workstreams: ClientWorkstreamData[] } = yield call(
      fetchClientWorkstreamsData,
      payload.clientId
    );

    // Pre-populate the pseudo-workstream's contacts into the per-workstream cache so rows for it
    // don't fire a separate /client-contacts fetch — the backend already inlined them here.
    for (const ws of department.workstreams) {
      if (!ws.isWorkstream) {
        yield put(
          setClientWorkstreamContactsData({
            workstreamId: ws.zoho_crm_id,
            clientId: payload.clientId,
            data: ws.contacts,
            state: DataState.Fulfilled,
          })
        );
      }
    }

    yield put(
      setClientWorkstreamsData({ clientId: payload.clientId, data: department.workstreams, state: DataState.Fulfilled })
    );
  } catch (error) {
    yield put(setClientWorkstreamsData({ clientId: payload.clientId, data: null, state: DataState.Rejected }));
    yield put(setClientWorkstreamContactsData({ ...pseudo, data: null, state: DataState.Rejected }));
  }
}

function* addClientContactAsync({ payload }: ReturnType<typeof addClientContact>) {
  let hasErorr = false;
  try {
    yield put(setEditClientDataState(DataState.Pending));
    yield put(setClientSubProjectNetworkError(null));
    const response: AxiosResponse<{ data: { client_dashboard_user_id: string; zoho_id: string } }> = yield call(
      postClientContact,
      payload,
      payload.shouldCreateDashboardUser
    );
    const clientDashboardUserId = response.data.data.client_dashboard_user_id;
    const id = response.data.data.zoho_id;
    const setClientSubProjectAction = addClientContactStore({
      ...payload,
      id,
      isRegisteredInClientDashboard: payload.shouldCreateDashboardUser,
      clientDashboardUserId,
    });
    yield put(setClientSubProjectAction);
  } catch (error) {
    yield put(setEditClientDataState(DataState.Rejected));
    hasErorr = true;
    const e = getErrorInfo(error);
    const setClientSubProjectErorrAction = setClientSubProjectNetworkError(e?.message || null);
    yield put(setClientSubProjectErorrAction);
    yield put(displayNotification('Failed to create new client contact'));
  } finally {
    if (!hasErorr) {
      yield put(setEditClientDataState(DataState.Fulfilled));
      const setClientSubProjectErorrAction = setClientSubProjectNetworkError(null);
      yield put(setClientSubProjectErorrAction);
      yield put(
        displayNotification('Add client request successful, it may take few minutes to appear it on interface')
      );
      yield call(trackUserEvent, { actionType: 'ADD_CONTACT', data: { contactType: payload.rmTypeOfContact } });
    }
  }
}

function* editClientContactAsync({ payload }: ReturnType<typeof editClientContact>) {
  let hasErorr = false;
  try {
    yield put(setEditClientDataState(DataState.Pending));
    const response: AxiosResponse<{ data: { client_dashboard_user_id: string } }> = yield call(
      patchClientContact,
      payload,
      payload.shouldCreateDashboardUser
    );
    const clientDashboardUserId = response.data.data.client_dashboard_user_id;
    const setClientSubProjectAction = editClientContactStore({
      ...payload,
      isRegisteredInClientDashboard: payload.shouldCreateDashboardUser,
      clientDashboardUserId,
    });
    yield put(setClientSubProjectAction);
  } catch (error) {
    yield put(setEditClientDataState(DataState.Rejected));
    hasErorr = true;
    const e = getErrorInfo(error);
    const setClientSubProjectErorrAction = setClientSubProjectNetworkError(e?.message || null);
    yield put(setClientSubProjectErorrAction);
    yield put(displayNotification('Failed to edit client information'));
  } finally {
    if (!hasErorr) {
      const setClientSubProjectErorrAction = setClientSubProjectNetworkError(null);
      yield put(setClientSubProjectErorrAction);
      yield put(setEditClientDataState(DataState.Fulfilled));
      yield put(displayNotification('Successfully edited!'));
      yield call(trackUserEvent, { actionType: 'EDIT_CONTACT', data: { contactType: payload.rmTypeOfContact } });
    }
  }
}

export function* getClientWorkstreamContactsDataAsync({ payload }: ReturnType<typeof getClientWorkstreamContactsData>) {
  if (!payload || !payload.workstreamId || !payload.clientId) {
    return;
  }
  yield put(setClientWorkstreamDataLoading(payload));
  try {
    const response: ClientContactData[] = yield call(
      fetchClientWorkstreamContactsData,
      payload.workstreamId,
      payload.clientId
    );
    yield put(setClientWorkstreamContactsData({ ...payload, data: response, state: DataState.Fulfilled }));
  } catch (error) {
    // F-S1: don't collapse a failed fetch into an empty list — the row needs to tell "no
    // contacts" and "couldn't load contacts" apart. Not cached (no fetchedAt), so the next
    // mount retries.
    yield put(setClientWorkstreamContactsData({ ...payload, data: null, state: DataState.Rejected }));
  }
}

function* watchGetClientContactData() {
  yield takeLatest(getClientContactData.type, getClientContactDataAsync);
  yield takeLatest(getClientWorkstreamsData.type, getClientWorkstreamsDataAsync);
  yield takeLatest(addClientContact.type, addClientContactAsync);
  yield takeLatest(editClientContact.type, editClientContactAsync);
  yield takeEvery(getClientWorkstreamContactsData.type, getClientWorkstreamContactsDataAsync);
}

export function* clientContactPageSaga(): Generator {
  yield all([watchGetClientContactData()]);
}
